/**
 * How much of the unprompted-interruption budget this session has spent.
 *
 * ⛔ THE CAP USED TO LIVE ON THE RENDERER INSTANCE. `PEEK_SELF_OPEN_SIGNAL_CAP`
 * and `PEEK_SIGNAL_COOLDOWN_MS` were checked against instance fields, and
 * Parikara remounts the widget on every route change — so both reset constantly.
 * Measured 2026-09-21 over 15 hours: 1802 self-opens across 722 visitors under a
 * cap of 2, with one visitor interrupted 28 times.
 *
 * sessionStorage for the same reason `self-open-trigger.ts` and `skip-memory.ts`
 * use it: it survives a remount and a reload, and dies with the tab. It does NOT
 * survive a new tab — that is the honest scope of "session" in this SDK.
 */
const PREFIX = "bc_self_open_budget:";

export interface SelfOpenBudget {
  /** Unprompted opens already spent. */
  signals: number;
  /** `Date.now()` of the last one, for the cooldown. */
  lastAt: number;
}

const EMPTY: SelfOpenBudget = { signals: 0, lastAt: 0 };

/** A finite, non-negative number, or 0. ⛔ `NaN >= CAP` is false, so a corrupt
 *  value that reached the comparison would switch the cap OFF. */
function count(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : 0;
}

export function readSelfOpenBudget(ns: string): SelfOpenBudget {
  try {
    const raw = window.sessionStorage.getItem(PREFIX + ns);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<SelfOpenBudget>;
    return { signals: count(parsed.signals), lastAt: count(parsed.lastAt) };
  } catch {
    return { ...EMPTY };
  }
}

export function writeSelfOpenBudget(ns: string, b: SelfOpenBudget): void {
  try {
    window.sessionStorage.setItem(PREFIX + ns, JSON.stringify({
      signals: count(b.signals), lastAt: count(b.lastAt),
    }));
  } catch {
    /* Private mode / blocked storage: the instance fields still cap this mount. */
  }
}

/**
 * ⭐ Owner ruling 2026-10-08: the QUALIFIED push budget is ONE PER MATCH PER
 * SESSION, at least `PEEK_QUALIFIED_GAP_MS` apart, with NO total session cap.
 * The 2-per-session cap spent the whole budget on the first two matches a
 * visitor opened; every covered match after that never pushed its card.
 *
 * The matches already pushed live here, beside the session budget, for the same
 * remount reason: an instance field resets on every Parikara route change.
 * ⛔ NOT TRUNCATED (Codex review 2026-10-08): dropping the oldest id let that
 * match push again after a remount. It dies with the tab; ids are ~10 chars.
 */
const MATCHES_PREFIX = "bc_self_open_matches:";

/** ⛔ CROSS-TAB (checkpoint 2026-10-08). sessionStorage is per TAB, the session
 *  id is per BROWSER: 32 of 671 pushes measured on the box were a second push of
 *  the same match in the same session, and in 12 the widget had refused that
 *  match as `match_spent` in between — one tab knew, the other did not. Brave
 *  probe: the record survives SPA navigation, reload and casino round-trips, so
 *  the second tab is the gap. localStorage is shared by tabs; an entry counts
 *  for SHARED_SPENT_MS — the visit rule's activity gap (3 h). */
const SHARED_PREFIX = "bc_self_open_matches_shared:";
/** When ANY tab last pushed (Codex review of d0249fdf): the 60 s gap is the
 *  visitor's, not the tab's — tab B must not push the moment tab A did. */
const SHARED_LAST_PREFIX = "bc_self_open_last_shared:";

export function readSharedLastPush(ns: string): number {
  try {
    const v = Number(window.localStorage.getItem(SHARED_LAST_PREFIX + ns));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}
export const SHARED_SPENT_MS = 3 * 60 * 60 * 1000;

function readShared(ns: string, now: number, prefix: string = SHARED_PREFIX): Record<string, number> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(prefix + ns) || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [id, at] of Object.entries(parsed)) {
      if (id && typeof at === "number" && Number.isFinite(at) && now - at < SHARED_SPENT_MS) out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

export function readPushedMatches(ns: string, now: number = Date.now()): string[] {
  let tab: string[] = [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(MATCHES_PREFIX + ns) || "[]");
    tab = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string" && x !== "") : [];
  } catch {
    tab = [];
  }
  return [...new Set([...tab, ...Object.keys(readShared(ns, now))])];
}

export function markMatchPushed(ns: string, eventId: string, now: number = Date.now()): void {
  if (!eventId) return;
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(MATCHES_PREFIX + ns) || "[]");
    const ids = (Array.isArray(raw) ? raw : []).filter((x: unknown) => typeof x === "string" && x !== eventId);
    ids.push(eventId);
    window.sessionStorage.setItem(MATCHES_PREFIX + ns, JSON.stringify(ids));
  } catch {
    /* Private mode / blocked storage: the renderer's instance set still covers this mount. */
  }
  try {
    // Expired entries are dropped on every write, so the shared record stays small.
    const shared = readShared(ns, now);
    shared[eventId] = now;
    window.localStorage.setItem(SHARED_PREFIX + ns, JSON.stringify(shared));
    window.localStorage.setItem(SHARED_LAST_PREFIX + ns, String(now));
  } catch {
    /* blocked storage: the tab record above still holds */
  }
}

/** ⭐ The red badge's «seen» (owner ruling 2026-10-06: off once the match's card
 *  was shown). It lived on the renderer instance, so a reload, an SPA remount or a
 *  second tab badged a match the visitor had already seen — found by the Brave
 *  checkpoint 2026-10-09. Same scope as the push record: shared for 3 h. */
const BADGE_SEEN_PREFIX = "bc_badge_seen_shared:";

export function isBadgeSeen(ns: string, eventId: string, now: number = Date.now()): boolean {
  return !!eventId && eventId in readShared(ns, now, BADGE_SEEN_PREFIX);
}

export function markBadgeSeenShared(ns: string, eventId: string, now: number = Date.now()): void {
  if (!eventId) return;
  try {
    const seen = readShared(ns, now, BADGE_SEEN_PREFIX);
    seen[eventId] = now;
    window.localStorage.setItem(BADGE_SEEN_PREFIX + ns, JSON.stringify(seen));
  } catch {
    /* blocked storage: the instance field still covers this mount */
  }
}
